/**
 * GTM Surface Layer — The Single Source of Truth
 *
 * Ten emit functions mapping to GA4 e-commerce funnel events.
 * Each function:
 *   1. Builds Tablz public event (tablz namespace)
 *   2. Builds GA4 top-level fields (ecommerce, page_*, etc.)
 *   3. Pushes a single combined object to dataLayer
 *
 * GTM reads from dataLayer. GA4 tags read the
 * top-level ecommerce/page fields. Custom Tablz tags read from tablz.*.
 *
 * No PII in any data layer push.
 */

import { TDR } from 'tdr-common';
import { pushToDataLayer, getVenueSlugFromURL, pushOrBufferReplayableEvent } from '../dataLayer';
import { PublicDataLayerEvent, CommerceData } from '../types';
import { warn } from '../logger';
import {
  formatGA4PageView,
  formatGA4Search,
  formatGA4ViewItemList,
  formatGA4ViewItem,
  formatGA4SelectItem,
  formatGA4AddToCart,
  formatGA4BeginCheckout,
  formatGA4AddPaymentInfo,
  formatGA4Purchase
} from './ga4';
import {
  VenueContext,
  SurfacePageViewParams,
  SurfaceSearchParams,
  SurfaceViewItemListParams,
  SurfaceViewItemParams,
  SurfaceSelectItemParams,
  SurfaceAddToCartParams,
  SurfaceBeginCheckoutParams,
  SurfaceAddPaymentInfoParams,
  SurfacePurchaseParams,
  SurfaceBookingConfirmationParams
} from './types';

// --- Helpers ---

function buildBaseEvent(venue: VenueContext, eventName: PublicDataLayerEvent['event']): PublicDataLayerEvent {
  const venueSlug = getVenueSlugFromURL();

  if (!venueSlug) {
    warn('[GTM] Could not determine venue_slug from URL:', window.location.pathname);
  }

  return {
    event: eventName,
    tablz: {
      schema_version: '1.0',
      env: 'production',
      venue_slug: venueSlug || 'unknown',
      ...(venue?.venueId && { venue_id: venue.venueId }),
      event_id: '',
      timestamp_ms: 0,
      session_id: '',
      consent: {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      }
    }
  };
}

/** Map a TDR.Reservation.ItemType to a clean item_category string. */
export function itemTypeToCategory(type: TDR.Reservation.ItemType): string {
  switch (type) {
    case TDR.Reservation.ItemType.Table: return 'Reservation';
    case TDR.Reservation.ItemType.AddOn: return 'Add-on';
    case TDR.Reservation.ItemType.Deposit: return 'Deposit';
    case TDR.Reservation.ItemType.Experience: return 'Experience';
    default: return 'Reservation';
  }
}

// --- Emit Functions ---

/** 1. page_view — Route navigation */
export function emitPageView(venue: VenueContext, params: SurfacePageViewParams): void {
  const event = buildBaseEvent(venue, 'page_view');
  event.tablz.page = {
    path: params.path,
    title: params.title,
    referrer: typeof document !== 'undefined' ? document.referrer : ''
  };

  const ga4 = formatGA4PageView(params);
  pushOrBufferReplayableEvent(event, ga4);
}

/** 2. search — "Show Results" CTA clicked */
export function emitSearch(venue: VenueContext, params: SurfaceSearchParams): void {
  const event = buildBaseEvent(venue, 'search');
  event.tablz.search = {
    party_size: params.partySize,
    date: params.date,
    time: params.time,
    results_count: params.resultsCount
  };

  pushToDataLayer(event, formatGA4Search(venue, params));
}

/** 3. view_item_list — Table inventory loaded */
export function emitViewItemList(venue: VenueContext, params: SurfaceViewItemListParams): void {
  const event = buildBaseEvent(venue, 'view_item_list');
  event.tablz.commerce = {
    currency: venue.currency,
    items: params.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category
    }))
  };

  pushToDataLayer(event, formatGA4ViewItemList(venue, params.items, params.listId, params.listName));
}

/** 4. view_item — Table detail drawer opened */
export function emitViewItem(venue: VenueContext, params: SurfaceViewItemParams): void {
  const event = buildBaseEvent(venue, 'view_item');
  const commerce: CommerceData = {
    currency: venue.currency,
    items: [{
      item_id: params.itemId,
      item_name: params.itemName,
      item_category: params.itemCategory,
      quantity: 1,
      ...(params.price !== undefined ? { price: params.price } : {})
    }],
    ...(params.price !== undefined ? { value: params.price } : {})
  };
  event.tablz.commerce = commerce;

  pushToDataLayer(event, formatGA4ViewItem(venue, params));
}

/** 5. select_item — "Select Table" CTA clicked (before checkout creation) */
export function emitSelectItem(venue: VenueContext, params: SurfaceSelectItemParams): void {
  const event = buildBaseEvent(venue, 'select_item');
  const commerce: CommerceData = {
    currency: venue.currency,
    items: [{
      item_id: params.itemId,
      item_name: params.itemName,
      item_category: params.itemCategory,
      quantity: 1,
      ...(params.price !== undefined ? { price: params.price } : {})
    }],
    ...(params.price !== undefined ? { value: params.price } : {})
  };
  event.tablz.commerce = commerce;
  if (params.guests || params.date || params.time) {
    event.tablz.booking = {
      id: '',
      venue_id: venue.venueId,
      venue_name: venue.venueName,
      guests: params.guests || 0,
      date: params.date || '',
      time: params.time || ''
    };
  }

  pushToDataLayer(event, formatGA4SelectItem(venue, params));
}

/** 6. begin_checkout — Checkout session created */
export function emitBeginCheckout(venue: VenueContext, params: SurfaceBeginCheckoutParams): void {
  const event = buildBaseEvent(venue, 'begin_checkout');
  event.tablz.commerce = {
    currency: venue.currency,
    value: params.value,
    items: params.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: 'Reservation',
      price: item.price,
      quantity: item.quantity
    }))
  };
  event.tablz.checkout_id = params.checkoutId;
  if (params.guests || params.date || params.time) {
    event.tablz.booking = {
      id: '',
      venue_id: venue.venueId,
      venue_name: venue.venueName,
      guests: params.guests || 0,
      date: params.date || '',
      time: params.time || ''
    };
  }

  pushToDataLayer(event, formatGA4BeginCheckout(venue, { value: params.value, items: params.items }));
}

/** 7. add_to_cart — Ecommerce add-on section proceed (only fires with items) */
export function emitAddToCart(venue: VenueContext, params: SurfaceAddToCartParams): void {
  if (!params.items || params.items.length === 0) {
    return;
  }

  const value = params.items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const event = buildBaseEvent(venue, 'add_to_cart');
  event.tablz.commerce = {
    currency: venue.currency,
    value,
    items: params.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: 'Add-on',
      price: item.price,
      quantity: item.quantity
    }))
  };
  event.tablz.checkout_id = params.checkoutId;
  if (params.guests || params.date || params.time) {
    event.tablz.booking = {
      id: '',
      venue_id: venue.venueId,
      venue_name: venue.venueName,
      guests: params.guests || 0,
      date: params.date || '',
      time: params.time || ''
    };
  }

  pushToDataLayer(event, formatGA4AddToCart(venue, { items: params.items }));
}

/** 8. add_payment_info — Payment submitted */
export function emitAddPaymentInfo(venue: VenueContext, params: SurfaceAddPaymentInfoParams): void {
  const event = buildBaseEvent(venue, 'add_payment_info');
  event.tablz.commerce = {
    currency: venue.currency,
    value: params.value
  };
  event.tablz.checkout_id = params.checkoutId;
  if (params.guests || params.date || params.time) {
    event.tablz.booking = {
      id: '',
      venue_id: venue.venueId,
      venue_name: venue.venueName,
      guests: params.guests || 0,
      date: params.date || '',
      time: params.time || ''
    };
  }

  pushToDataLayer(event, formatGA4AddPaymentInfo(venue, { value: params.value }));
}

/** 9. purchase — Booking confirmed */
export function emitPurchase(venue: VenueContext, params: SurfacePurchaseParams): void {
  const event = buildBaseEvent(venue, 'purchase');
  event.tablz.commerce = {
    transaction_id: params.transactionId,
    currency: venue.currency,
    value: params.value,
    ...(params.tax !== undefined && { tax: params.tax }),
    items: params.items.map((item) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: item.category || 'Reservation',
      price: item.price,
      quantity: item.quantity
    }))
  };
  event.tablz.booking = {
    id: params.bookingId,
    venue_id: venue.venueId,
    venue_name: venue.venueName,
    guests: params.guests || 0,
    date: params.date || '',
    time: params.time || ''
  };

  const ga4Items = params.items.map((item) => ({
    id: item.id,
    name: item.name,
    category: item.category || 'Reservation',
    price: item.price,
    quantity: item.quantity
  }));

  pushToDataLayer(event, formatGA4Purchase(venue, {
    transactionId: params.transactionId,
    value: params.value,
    tax: params.tax,
    items: ga4Items
  }));
}

/** 10. booking_confirmation — Booking confirmed (fires for all bookings, including free) */
export function emitBookingConfirmation(venue: VenueContext, params: SurfaceBookingConfirmationParams): void {
  const event = buildBaseEvent(venue, 'tablz_booking_confirmation');
  event.tablz.booking = {
    id: params.bookingId,
    venue_id: venue.venueId,
    venue_name: venue.venueName,
    guests: params.guests || 0,
    date: params.date || '',
    time: params.time || ''
  };

  pushToDataLayer(event);
}
